'use client'
import { Button, type ButtonColor, type LegacyButtonColor } from '@tebuto/buttons/Button'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { cn } from '@tebuto/lib/utils'

import type { JSX } from 'react'

// Renamed Buttons -> FormActions (avoids collision with `Button`). The old name
// and prop names keep working during the deprecation window; see refactor spec.
interface FormActionsProps {
    // ── preferred API ──
    submitLabel?: string
    cancelLabel?: string
    /** Explicit cancel handler; when omitted the cancel button is a form reset. */
    onCancel?: () => void
    showReset?: boolean
    submitColor?: ButtonColor | LegacyButtonColor
    submitVariant?: 'solid' | 'outline' | 'ghost'
    loading?: boolean
    submitEnabled?: boolean
    cancelEnabled?: boolean
    className?: string
    submitButtonClassName?: string
    resetButtonClassName?: string
    submitButtonTestId?: string
    resetButtonTestId?: string

    // ── deprecated aliases (mapped through) ──
    /** @deprecated use `showReset` */
    showResetButton?: boolean
    /** @deprecated use `cancelEnabled` */
    resetButtonEnabled?: boolean
    /** @deprecated use `submitEnabled` */
    saveButtonEnabled?: boolean
    /** @deprecated use `submitColor` */
    saveButtonColor?: ButtonColor | LegacyButtonColor
    /** @deprecated cancel is now `variant="outline"`; this is ignored */
    saveButtonOutline?: boolean
    /** @deprecated use `cancelLabel` */
    resetButtonLabel?: string
    /** @deprecated use `submitLabel` */
    saveButtonLabel?: string
}

export function FormActions(props: FormActionsProps): JSX.Element {
    const { t } = useComponentTranslation()

    // Resolve preferred names, falling back to deprecated aliases.
    const showReset = props.showReset ?? props.showResetButton ?? true
    const cancelEnabled = props.cancelEnabled ?? props.resetButtonEnabled ?? true
    const submitEnabled = props.submitEnabled ?? props.saveButtonEnabled ?? true
    const submitColor = props.submitColor ?? props.saveButtonColor ?? 'primary'
    const submitVariant = props.submitVariant ?? (props.saveButtonOutline ? 'outline' : 'solid')
    const loading = props.loading ?? false
    const cancelLabel = props.cancelLabel ?? props.resetButtonLabel ?? t('discard')
    const submitLabel = props.submitLabel ?? props.saveButtonLabel ?? t('save')

    const submitButtonCss = cn(
        'w-full justify-center sm:w-max sm:justify-start',
        submitEnabled || loading ? '' : 'opacity-30 cursor-not-allowed',
        props.submitButtonClassName
    )
    const cancelButtonCss = cn(
        'w-full justify-center sm:w-max sm:justify-start',
        !cancelEnabled && 'cursor-not-allowed opacity-30',
        props.resetButtonClassName
    )

    return (
        <div
            className={cn(
                'flex w-full flex-col gap-3 sm:flex-row sm:items-center',
                !showReset ? 'sm:justify-end' : 'sm:justify-between',
                props.className
            )}
        >
            {showReset && (
                <Button
                    type={props.onCancel ? 'button' : 'reset'}
                    onClick={props.onCancel}
                    variant="outline"
                    color="neutral"
                    tabIndex={0}
                    disabled={!cancelEnabled}
                    className={cancelButtonCss}
                    data-testid={props.resetButtonTestId}
                >
                    {cancelLabel}
                </Button>
            )}
            <Button
                type="submit"
                tabIndex={0}
                disabled={!submitEnabled}
                isLoading={loading}
                color={submitColor}
                variant={submitVariant}
                className={submitButtonCss}
                data-testid={props.submitButtonTestId}
            >
                {submitLabel}
            </Button>
        </div>
    )
}

/** @deprecated import `FormActions` instead. */
const FormButtons = FormActions
export default FormActions
export { FormButtons }
